// src/services/userProfileService.ts
import { supabase } from '../lib/supabase'
import { UserProfile } from '../types'

export interface UpdateProfileData {
  first_name?: string
  last_name?: string
  phone?: string
  avatar_url?: string
}

/**
 * Get user profile by ID
 */
export const getUserProfile = async (
  userId: string
): Promise<UserProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single()

    if (error) {
      console.error('Error fetching user profile:', error)
      return null
    }

    return data as UserProfile
  } catch (error) {
    console.error('Unexpected error fetching user profile:', error)
    return null
  }
}

/**
 * Update user profile
 */
export const updateUserProfile = async (
  userId: string,
  updates: UpdateProfileData
): Promise<{ success: boolean; error?: string; data?: UserProfile }> => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .select()
      .single()

    if (error) {
      console.error('Error updating user profile:', error)
      return {
        success: false,
        error: error.message || 'Failed to update profile',
      }
    }

    return {
      success: true,
      data: data as UserProfile,
    }
  } catch (error) {
    console.error('Unexpected error updating user profile:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    }
  }
}

/**
 * Update only avatar URL
 */
export const updateAvatar = async (
  userId: string,
  avatarUrl: string | null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('user_profiles')
      .update({
        avatar_url: avatarUrl,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)

    if (error) {
      console.error('Error updating avatar:', error)
      return {
        success: false,
        error: error.message || 'Failed to update avatar',
      }
    }

    return { success: true }
  } catch (error) {
    console.error('Unexpected error updating avatar:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    }
  }
}

/**
 * Get current user's profile
 */
export const getCurrentUserProfile = async (): Promise<UserProfile | null> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      return null
    }

    return getUserProfile(user.id)
  } catch (error) {
    console.error('Error getting current user profile:', error)
    return null
  }
}
